import React, { useCallback, useMemo } from 'react';
import { Mono } from '@jotpsych/ui';
import { CheckCircleIcon } from '@jotpsych/ui/icons';
import { Box, Button, Chip, Stack, Tooltip, Typography } from '@mui/material';

import { formatDateOnly } from '../../../utils';
import {
  paidResolutionWorkHint,
  workStateChipColor,
  workStateChipLabel,
  workStateChipVariant,
} from '../../../utils/claimWorkPresentation';
import { type ColumnDef, DataTable, formatCents, PatientIdentity } from '../shared';
import { formatPayerStatusLine, ReasonCell } from '../work';

import { homeQueueColumns } from './homeQueueColumns';
import type { HomeQueueDefinition, HomeQueueRow } from './homeQueues';
import { ProcedureCodeChips } from './ProcedureCodeChips';
import { ReviewIssueCell } from './ReviewIssueCell';
import { renderWideColumnCell } from './wideColumnCells';

/**
 * One queue, as a card: the count, the five oldest rows, and one button that
 * starts the run. Presentational — every fetch, every navigation, and every
 * event belongs to `HomeContainer`.
 *
 * The body is the shared `DataTable`, not a table of its own. Home is a
 * SUMMARY of the same claim work the Claims tab lists, so a row here has to
 * read as the same row there: same header treatment, same 48px rows, same
 * hover, same `PatientIdentity`. The card is the full-width table under
 * Home's summary strip (GDN-4085), so each queue has its own column set
 * (`homeQueueColumns`): the date and who, and then the columns the Claims
 * tab shows for that queue.
 */
export interface QueueCardProps {
  queue: HomeQueueDefinition;
  /**
   * The queue has no work: the header stays, the table gives way to one line
   * saying so, and there is no run to start. Decided by the container
   * (`buildHomeQueueStrip`), never inferred here from an empty page, and
   * never over a failed read, which always shows as a failure.
   */
  clear?: boolean;
  /**
   * The server's count for the queue, hidden rows excluded. UNDEFINED when
   * the counts payload did not carry this key — an older server, a partial
   * rollout — which is not the same fact as zero and must not print as one.
   */
  count?: number;
  rows: HomeQueueRow[];
  /** This queue's own read failed. The other four may be fine. */
  error?: boolean;
  onRetry?: () => void;
  /**
   * There is a first step for the run to start at. False when every loaded
   * row is one the workbench cannot hold (a null `encounter_id`), which is
   * rows on the card but no queue to work; the button then says so.
   */
  canWorkQueue?: boolean;
  /**
   * The run behind the button is being read: the whole queue, page by page.
   * The button waits and says so; the rows stay clickable.
   */
  pending?: boolean;
  /** The organization's word for a patient ("Patient", "Client"). */
  subjectLabel: string;
  onOpenRow: (row: HomeQueueRow) => void;
  onWorkQueue: () => void;
  /**
   * The pointer entered a row. The container warms the workbench's first
   * fetch wave for that claim, exactly as the Claims worklist does
   * (GDN-3201) — the card and the worklist open the same workbench, so a
   * row here should not cost more than a row there.
   */
  onRowHover?: (row: HomeQueueRow) => void;
  /**
   * The pointer, or the keyboard, reached "Work this queue". The button
   * opens the run's FIRST STEP, which is a known claim before the click, so
   * it warms like a row does. Focus counts: a biller who tabs to the button
   * is as committed as one who points at it.
   */
  onWorkQueueHover?: () => void;
  /**
   * The row menu's slot (packet D's `QueueRowMenu`). Absent until it lands;
   * the slot exists now so wiring it is one prop, not a layout change.
   */
  renderRowEnd?: (row: HomeQueueRow) => React.ReactNode;
  /**
   * The Issues popover's content for a Claims to review row (GDN-3379),
   * handed the row and the cell's own headline. Absent, the cell is the one
   * line alone. Rendered only while the popover is open, so a container here
   * reads on open and not on row hover.
   */
  renderIssueDetail?: (row: HomeQueueRow, headline: string) => React.ReactNode;
  /** The Issues popover opened on a row, by hover or by focus. Once per open. */
  onIssueDetailOpen?: (row: HomeQueueRow) => void;
  renderReasonDetail?: (row: HomeQueueRow) => React.ReactNode;
  onReasonDetailOpen?: (row: HomeQueueRow) => void;
}

/* Every truncating caption below is `component="div"`. `noWrap` sets
   text-overflow: ellipsis, which has NO effect on an inline box, and
   Typography variant="caption" renders a span: without this the long blocker
   sentences clip square at the cell edge instead of trailing off. */
const formatRowDate = (value?: string | null): string =>
  formatDateOnly(value, { day: 'numeric', month: 'short' }, '-');

/** Why "Work this queue" is off while the card still shows rows. */
const NO_ROW_CAN_OPEN = 'None of these claims can open in the workbench';

/** The body of a queue with no work, in place of the table. */
const QueueClear: React.FC<{ queueKey: string }> = ({ queueKey }) => (
  <Stack
    data-testid={`home-queue-clear-${queueKey}`}
    spacing={1}
    sx={{
      alignItems: 'center',
      /* The colour after the shorthand: `border-top` resets its own colour
         to currentColor, so a `borderColor` written before it is lost. */
      borderTop: '1px solid',
      borderTopColor: 'divider',
      px: 2.5,
      py: 5,
      textAlign: 'center',
    }}
  >
    <CheckCircleIcon aria-hidden sx={{ color: 'success.dark', fontSize: 36 }} />
    <Typography sx={{ fontWeight: 700 }} variant="body1">
      Nothing to do here right now.
    </Typography>
  </Stack>
);

export const QueueCard: React.FC<QueueCardProps> = ({
  queue,
  clear = false,
  count,
  rows,
  error = false,
  onRetry,
  canWorkQueue = true,
  pending = false,
  subjectLabel,
  onOpenRow,
  onWorkQueue,
  onRowHover,
  onWorkQueueHover,
  renderRowEnd,
  renderIssueDetail,
  onIssueDetailOpen,
  renderReasonDetail,
  onReasonDetailOpen,
}) => {
  /* A failed read is a failure first: it never reads as clear. */
  const showClear = clear && !error;
  /* A count the server did not send is not a zero. Falling back to the rows
     in hand is the only number this card can stand behind, and the footer
     below then says nothing rather than claiming a page it cannot see. */
  const shownCount = showClear ? 0 : (count ?? rows.length);
  /* The one disabled state that needs a word: rows on the card, none the
     workbench can hold. A failed read explains itself in the body and an
     empty queue has nothing to explain. */
  const noRowCanOpen = !error && rows.length > 0 && !canWorkQueue;
  const workQueueButton = (
    <Button
      disabled={error || rows.length === 0 || !canWorkQueue}
      loading={pending}
      onClick={onWorkQueue}
      onFocus={onWorkQueueHover}
      onMouseEnter={onWorkQueueHover}
      size="small"
      sx={{ flexShrink: 0, fontWeight: 600, textTransform: 'none' }}
      variant="contained"
    >
      Work this queue
    </Button>
  );
  const columns = useMemo<ColumnDef[]>(
    () => homeQueueColumns(queue, subjectLabel),
    [queue, subjectLabel],
  );

  const tableRows = useMemo(
    () =>
      rows.map(item => ({
        _raw: item,
        id: item.claim_id,
        payer_name: item.payer_name ?? '-',
        service_date: formatRowDate(item.service_date),
      })),
    [rows],
  );

  const renderCell = useCallback(
    (column: ColumnDef, row: Record<string, unknown>) => {
      const item = row._raw as HomeQueueRow;
      if (column.key === 'patient_name') {
        return <PatientIdentity patientId={item.patient_id} patientName={item.patient_name} />;
      }
      const wideCell = renderWideColumnCell(column.key, item);
      if (wideCell !== null) return wideCell;
      if (column.key !== 'detail') return null;

      if (queue.key === 'submit') {
        const codes = item.procedure_codes ?? [];
        return (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', minWidth: 0 }}>
            {/* The shared Mono primitive, not a local `fontFamily`: it carries
                the theme's mono stack AND `tabular-nums`, which is what makes
                a column of charges line up. The SIZE stays the DataTable's
                own, so a row here reads as the same row in the Claims
                worklist. */}
            <Mono sx={{ fontSize: '0.8125rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
              {formatCents(item.total_charge_cents ?? 0)}
            </Mono>
            {codes.length > 0 && <ProcedureCodeChips codes={codes} />}
          </Stack>
        );
      }

      if (queue.key === 'review') {
        return (
          <ReviewIssueCell
            item={item}
            renderDetail={renderIssueDetail}
            onDetailOpen={onIssueDetailOpen}
          />
        );
      }

      if (queue.key === 'awaiting_payer') {
        const caption = formatPayerStatusLine(item.last_payer_status);
        return (
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>
              {item.days_silent == null ? 'No payer activity' : `${item.days_silent} days`}
            </Typography>
            {caption && (
              <Typography color="text.secondary" component="div" noWrap variant="caption">
                {caption}
              </Typography>
            )}
          </Box>
        );
      }

      return (
        <ReasonCell
          item={item}
          content={renderReasonDetail?.(item)}
          onOpen={() => onReasonDetailOpen?.(item)}
          outcome={
            <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
            <Chip
              color={workStateChipColor(item)}
              label={workStateChipLabel(item)}
              size="small"
              variant={workStateChipVariant(item)}
              sx={{ flexShrink: 0, fontWeight: 600 }}
            />
            {paidResolutionWorkHint(item) ? <Typography variant="caption" color="text.secondary">{paidResolutionWorkHint(item)}</Typography> : null}
            </Stack>
          }
        />
      );
    },
    [onIssueDetailOpen, onReasonDetailOpen, queue.key, renderIssueDetail, renderReasonDetail],
  );

  return (
    <Box
      data-testid={`home-queue-card-${queue.key}`}
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '10px',
        /* clip, not hidden: the DataTable inside relies on the same choice so
           its header can stick to the window rather than to this box. */
        overflow: 'clip',
      }}
    >
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start', px: 2.5, py: 1.75 }}>
        <Box
          aria-hidden
          sx={{
            bgcolor: queue.color,
            borderRadius: 4,
            flexShrink: 0,
            mt: 0.35,
            height: 20,
            width: 3,
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
            <Typography sx={{ fontWeight: 700 }} variant="subtitle1">
              {queue.label}
            </Typography>
            <Typography
              data-testid={`home-queue-count-${queue.key}`}
              sx={{
                color: showClear ? 'grey.600' : queue.color,
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
              }}
              variant="h5"
            >
              {error ? '-' : shownCount}
            </Typography>
          </Stack>
          <Typography color="text.secondary" variant="caption">
            {queue.caption}
          </Typography>
        </Box>
        {/* An empty queue has no run to start, so it offers none. */}
        {showClear ? null : noRowCanOpen ? (
          /* A disabled button fires no pointer events, so the span carries
             the tooltip, as every disabled control in the app does. */
          <Tooltip title={NO_ROW_CAN_OPEN}>
            <span>{workQueueButton}</span>
          </Tooltip>
        ) : (
          workQueueButton
        )}
      </Stack>

      {/* A failed queue says so. Rendering the table's own "No results" here
          would make a 500 look exactly like an empty queue, which is the one
          reading a biller must never be given. An empty queue says so too,
          but only on the container's word that it is empty. */}
      {showClear ? (
        <QueueClear queueKey={queue.key} />
      ) : error ? (
        <Stack
          data-testid={`home-queue-error-${queue.key}`}
          spacing={1}
          sx={{
            alignItems: 'center',
            borderColor: 'divider',
            borderTop: '1px solid',
            px: 2.5,
            py: 4,
          }}
        >
          <Typography color="text.secondary" variant="body2">
            This queue could not be loaded.
          </Typography>
          {onRetry && (
            <Button onClick={onRetry} size="small" sx={{ textTransform: 'none' }}>
              Try again
            </Button>
          )}
        </Stack>
      ) : (
        <DataTable
          columns={columns}
          embedded
          renderCell={renderCell}
          renderRowEnd={renderRowEnd ? row => renderRowEnd(row._raw as HomeQueueRow) : undefined}
          rows={tableRows}
          onRowClick={row => onOpenRow(row._raw as HomeQueueRow)}
          onRowHover={onRowHover ? row => onRowHover(row._raw as HomeQueueRow) : undefined}
        />
      )}

      {!showClear && !error && shownCount > rows.length && rows.length > 0 && (
        <Typography
          color="text.secondary"
          sx={{
            borderColor: 'divider',
            borderTop: '1px solid',
            display: 'block',
            px: 2.5,
            py: 1.25,
          }}
          variant="caption"
        >
          {`Showing the ${rows.length} oldest of ${shownCount}`}
        </Typography>
      )}
    </Box>
  );
};
